import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { en, type Translations } from "./en";
import { uk } from "./uk";

export type Lang = "en" | "uk";

// Languages offered to visitors. English is fully wired up but hidden for
// now — add "en" back here to re-enable it (the toggle reappears on its own).
export const AVAILABLE_LANGS: Lang[] = ["uk"];
const DEFAULT_LANG: Lang = "uk";

const catalogs: Record<Lang, Translations> = { en, uk };
const STORAGE_KEY = "lang";

function detectLang(): Lang {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if ((stored === "en" || stored === "uk") && AVAILABLE_LANGS.includes(stored)) {
      return stored;
    }
  } catch {
    // localStorage unavailable (privacy mode) — fall through to default
  }
  return DEFAULT_LANG;
}

interface I18nContextValue {
  lang: Lang;
  setLang: (lang: Lang) => void;
  t: Translations;
  /** BCP 47 locale for date/number formatting. */
  locale: string;
}

const I18nContext = createContext<I18nContextValue | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(detectLang);

  const setLang = (next: Lang) => {
    setLangState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Non-persistent, but the toggle still works for this visit
    }
  };

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  return (
    <I18nContext.Provider
      value={{ lang, setLang, t: catalogs[lang], locale: lang === "uk" ? "uk-UA" : "en-US" }}
    >
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used within I18nProvider");
  return ctx;
}
